/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOrganizationRemoteSessionIssuerEMAServicesRequest,
  ListOrganizationRemoteSessionIssuerEMAServicesSecurity,
} from "../models/operations/listorganizationremotesessionissueremaservices.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationRemoteSessionIssuerEMAServicesQuery,
  OrganizationRemoteSessionIssuerEMAServicesQueryData,
  prefetchOrganizationRemoteSessionIssuerEMAServices,
  queryKeyOrganizationRemoteSessionIssuerEMAServices,
} from "./organizationRemoteSessionIssuerEMAServices.core.js";
export {
  buildOrganizationRemoteSessionIssuerEMAServicesQuery,
  type OrganizationRemoteSessionIssuerEMAServicesQueryData,
  prefetchOrganizationRemoteSessionIssuerEMAServices,
  queryKeyOrganizationRemoteSessionIssuerEMAServices,
};

export type OrganizationRemoteSessionIssuerEMAServicesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listEMAServices organizationRemoteSessionIssuers
 *
 * @remarks
 * List every service identity chaining reaches in the caller's organization: each vendor authorization server that any identity chaining binding or saved EMA provider order names, with the organization-wide provider order, when the vendor last accepted an assertion from each provider across every binding, and the MCP servers that chain through it: counts include every server, the list only those the caller has mcp:read on. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuerEMAServices(
  request?: ListOrganizationRemoteSessionIssuerEMAServicesRequest | undefined,
  security?: ListOrganizationRemoteSessionIssuerEMAServicesSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationRemoteSessionIssuerEMAServicesQueryData,
    OrganizationRemoteSessionIssuerEMAServicesQueryError
  >,
): UseQueryResult<
  OrganizationRemoteSessionIssuerEMAServicesQueryData,
  OrganizationRemoteSessionIssuerEMAServicesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationRemoteSessionIssuerEMAServicesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listEMAServices organizationRemoteSessionIssuers
 *
 * @remarks
 * List every service identity chaining reaches in the caller's organization: each vendor authorization server that any identity chaining binding or saved EMA provider order names, with the organization-wide provider order, when the vendor last accepted an assertion from each provider across every binding, and the MCP servers that chain through it: counts include every server, the list only those the caller has mcp:read on. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuerEMAServicesSuspense(
  request?: ListOrganizationRemoteSessionIssuerEMAServicesRequest | undefined,
  security?: ListOrganizationRemoteSessionIssuerEMAServicesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationRemoteSessionIssuerEMAServicesQueryData,
    OrganizationRemoteSessionIssuerEMAServicesQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationRemoteSessionIssuerEMAServicesQueryData,
  OrganizationRemoteSessionIssuerEMAServicesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationRemoteSessionIssuerEMAServicesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationRemoteSessionIssuerEMAServicesData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: OrganizationRemoteSessionIssuerEMAServicesQueryData,
): OrganizationRemoteSessionIssuerEMAServicesQueryData | undefined {
  const key = queryKeyOrganizationRemoteSessionIssuerEMAServices(
    ...queryKeyBase,
  );

  return client.setQueryData<
    OrganizationRemoteSessionIssuerEMAServicesQueryData
  >(key, data);
}

export function invalidateOrganizationRemoteSessionIssuerEMAServices(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionIssuers",
      "listEMAServices",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationRemoteSessionIssuerEMAServices(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionIssuers",
      "listEMAServices",
    ],
  });
}
